import type { SignInSurface } from "./events";
import { useDesign } from "./store";

/**
 * "Open the AI layout sheet" requests from outside the Design view (the
 * onboarding guides' AI steps). The Design view owns the sheet and its
 * quota gate; a request made from another view switches to Design first
 * and is delivered once the Design view subscribes.
 */

export interface AiLayoutRequest {
  /** Sign-in surface to report if the sheet asks the visitor to sign in. */
  surface?: SignInSurface;
}

let pending: AiLayoutRequest | null = null;
const listeners = new Set<(req: AiLayoutRequest) => void>();

function flush() {
  if (!pending || listeners.size === 0) return;
  const req = pending;
  pending = null;
  for (const l of listeners) l(req);
}

export function requestAiLayout(req: AiLayoutRequest = {}): void {
  const s = useDesign.getState();
  if (s.view !== "design") s.setView("design");
  pending = req;
  flush();
}

export function subscribeAiLayoutRequests(listener: (req: AiLayoutRequest) => void): () => void {
  listeners.add(listener);
  if (pending) queueMicrotask(flush);
  return () => {
    listeners.delete(listener);
  };
}

/** Test-only: drop an undelivered request. */
export function _resetAiLayoutRequestsForTests(): void {
  pending = null;
}
